/**
 * Central API configuration, cookie credentials + optional legacy Bearer.
 */
import axios from 'axios';
import { getAuthToken } from '@/utils/auth';
import { BACKEND_URL } from './env';

export { BACKEND_URL };

/** Full URL to /api root */
export const API_BASE = `${BACKEND_URL}/api`;

/** Full URL to /api/auth */
export const AUTH_API = `${API_BASE}/auth`;

/**
 * Build a full API path against the backend origin.
 * @param {string} path - Path starting with /api/...
 */
export function apiUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${BACKEND_URL}${normalized}`;
}

function attachAuth(headers = {}) {
  const next = { ...headers };
  // TODO(remove-by-2026-10-01): Bearer fallback while migrating off localStorage JWT
  const token = getAuthToken();
  if (token && !next.Authorization) {
    next.Authorization = `Bearer ${token}`;
  }
  return next;
}

/**
 * Create an API client for a route prefix.
 */
export function createApiClient({ basePath = '/api', sameOrigin = false, withAuth = true } = {}) {
  const root = sameOrigin ? basePath : `${BACKEND_URL}${basePath}`;

  function url(path = '') {
    if (!path) return root;
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${root}${normalized}`;
  }

  function authHeaders(extra = {}) {
    if (!withAuth) return { ...extra };
    return attachAuth(extra);
  }

  async function get(path, options = {}) {
    return fetch(url(path), {
      ...options,
      method: 'GET',
      credentials: 'include',
      headers: authHeaders({ 'Content-Type': 'application/json', ...options.headers }),
    });
  }

  async function post(path, body, options = {}) {
    return fetch(url(path), {
      ...options,
      method: 'POST',
      credentials: 'include',
      headers: authHeaders({ 'Content-Type': 'application/json', ...options.headers }),
      body: JSON.stringify(body),
    });
  }

  async function patch(path, body, options = {}) {
    return fetch(url(path), {
      ...options,
      method: 'PATCH',
      credentials: 'include',
      headers: authHeaders({ 'Content-Type': 'application/json', ...options.headers }),
      body: JSON.stringify(body),
    });
  }

  function axiosInstance() {
    const instance = axios.create({
      baseURL: root,
      withCredentials: true,
    });
    if (withAuth) {
      instance.interceptors.request.use((config) => {
        config.withCredentials = true;
        config.headers = authHeaders(config.headers || {});
        return config;
      });
    }
    return instance;
  }

  return { root, url, get, post, patch, axios: axiosInstance };
}

/** Default client for most /api routes */
export const api = createApiClient();

/** Payments against API origin so httpOnly cookie is sent (not Vercel same-origin proxy). */
export const paymentsApi = createApiClient({ basePath: '/api/payments' });

/** Auth-specific axios base */
export const authApi = createApiClient({ basePath: '/api/auth' });
